import { ChangeDetectionStrategy, Component, inject, signal, TemplateRef, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Catalog } from "@front/m19-models";
import { IAlertComponent, IButtonDirective, MODAL_DATA, ModalRef, ModalService, ToasterService } from "@front/m19-ui";
import { Utils } from "@front/m19-utils";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";

@Component({
  selector: "app-bulk-asin-modal",
  imports: [IButtonDirective, FormsModule, TranslocoModule, IAlertComponent],
  templateUrl: "./bulk-asin-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkAsinModalComponent {
  protected readonly modalRef = inject(ModalRef);
  protected readonly data = inject<BulkAsinModalData>(MODAL_DATA);
  protected readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);
  private readonly toasterService = inject(ToasterService);

  @ViewChild("confirmAsinsModal") confirmAsinsModal!: TemplateRef<any>;
  confirmAsinsModalRef: ModalRef<boolean> | undefined;

  bulkInput = signal<string>("");
  bulkErrors = signal<{ asin: string | undefined; error: string }[] | undefined>(undefined);
  selectedAsins = signal<string[]>([]);

  processBulkInput() {
    this.bulkErrors.set([]);
    const asins = this.bulkInput()
      .trim()
      .split(/[\s,;\n]+/)
      .map((asin) => asin.trim().toUpperCase());
    const errors: { asin: string | undefined; error: string }[] = [];
    const toAdd: string[] = [];

    for (const asin of asins) {
      if (this.data.disabledAsins?.has(asin)) continue;
      if (!Utils.isValidAsin(asin)) {
        errors.push({ asin, error: this.translocoService.translate("seeds-edition-modal.invalid_asin") });
      } else if (this.data.customList && !this.data.customList.includes(asin)) {
        errors.push({ asin, error: this.translocoService.translate("strategy-asins.asin_not_in_list") });
      } else if (!this.data.allowOtherCatalog && !this.data.catalog!.contains(asin)) {
        errors.push({ asin, error: this.translocoService.translate("strategy-asins.asin_not_in_catalog") });
      } else if (!this.data.allowOtherCatalog && !this.data.catalog!.childAsins.has(asin)) {
        errors.push({ asin, error: this.translocoService.translate("strategy-asins.not_a_child_asin") });
      } else if (this.data.selectedAsins.includes(asin)) {
        errors.push({ asin, error: this.translocoService.translate("strategy-asins.asin_already_selected") });
      } else if (!toAdd.includes(asin)) {
        toAdd.push(asin);
      }
    }

    this.selectedAsins.set(toAdd);
    if (errors.length === 0) {
      this.emitBulkSelection();
    } else {
      this.bulkErrors.set(errors);
      this.checkErrorsAndConfirm();
    }
  }

  checkErrorsAndConfirm() {
    if (this.bulkErrors()?.length === 0) {
      this.emitBulkSelection();
    } else {
      this.confirmAsinsModalRef = this.modalService.openModal(this.confirmAsinsModal, {
        modalTitle: this.translocoService.translate("seeds-edition-modal.invalid_asin"),
      });
    }
  }

  emitBulkSelection() {
    this.toasterService.success(
      this.translocoService.translate("asins-selection.bulk_asin_success", [this.selectedAsins().length ?? 0]),
    );
    this.confirmAsinsModalRef?.close();
    this.modalRef.close(this.selectedAsins());
  }
}

export interface BulkAsinModalData {
  catalog: Catalog;
  disabledAsins: Set<string>;
  selectedAsins: string[];
  customList: string[] | undefined;
  allowOtherCatalog: boolean;
}
